import { inject, Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs/operators';

import { BENCHMARK_OPTIONS, DEFAULT_BENCHMARK_OPTION, DEFAULT_RESULTS_PER_PAGE, RESULTS_PER_PAGE_OPTIONS } from 'app/apps/discovery/app/constants';
import { DEFAULT_NUMBER_OF_RESULTS_REPORTS, NUMBER_OF_RESULTS_OPTIONS_REPORTS } from 'app/apps/reports/app/constants';
import { ALL_COMPARISONS, DEFAULT_COMPARE_TO, RESULTS_PER_PAGE_OPTIONS as STUDIO_RESULTS_PER_PAGE_OPTIONS, DEFAULT_RESULTS_PER_PAGE as STUDIO_DEFAULT_RESULTS_PER_PAGE } from 'app/apps/studio/app/constants';
import { QueryFilter } from 'app/hub-components/models/query-filter.model';
import { IQueryFilterWrapper } from '../../interfaces/query-filter.interface';
import { ColumnOrderEntry } from '../../interfaces/table-options.interface';
import { Query } from '../../models/analytics-api/query.model';
import { DateRangeValue } from '../../modules/date-range-picker/types';
import { serializeDateRangeValue } from '../../utils/date.util';
import { QueryMigrationService } from '../migration/query-migration.service';
import { PublicLinkService } from '../public-link/public-link.service';
import { SessionService } from '../session/session.service';
import { TrackingService } from '../tracking/tracking.service';

export interface OpenInReportsParams {
  query: Query;
  dateRange: DateRangeValue;
  globalFilters?: IQueryFilterWrapper[];
  globalFiltersVersion?: number;
  excludeFalsy?: boolean;
  includeTotals?: boolean;
  columnOrder?: ColumnOrderEntry[];
}

export interface OpenInStudioParams {
  tables: {
    query: Query;
    excludeFalsy?: boolean;
    showColumnAllPagesTotals?: boolean;
    dateRange?: DateRangeValue;
    resultsPerPage?: number;
    benchmark?: string;
    benchmarkDateRange?: DateRangeValue;
    columnOrder?: ColumnOrderEntry[];
  }[];
  dateRange: DateRangeValue;
  globalFilters?: IQueryFilterWrapper[];
  globalFiltersVersion?: number;
}

export interface OpenInDiscoveryParams {
  query: Query;
  dateRange: DateRangeValue;
  excludeFalsy?: boolean;
  globalFilters?: IQueryFilterWrapper[];
  resultsPerPage?: number;
  benchmark?: string;
  benchmarkDateRange?: DateRangeValue;
}

@Injectable()
export class CrossAppNavigationService {
  private sessionService = inject(SessionService);
  private trackingService = inject(TrackingService);
  private queryMigrationService = inject(QueryMigrationService);
  private publicLinkService = inject(PublicLinkService);

  public openInReports({ query, dateRange, globalFilters = [], excludeFalsy = false, includeTotals = false, columnOrder }: OpenInReportsParams): void {
    const queryVersion = this.queryMigrationService.currentVersion;
    const numberOfResults = NUMBER_OF_RESULTS_OPTIONS_REPORTS.find(o => o === query.limit) ?? DEFAULT_NUMBER_OF_RESULTS_REPORTS;
    query.limit = numberOfResults;
    const reportData = {
      name: 'New Report',
      settings: {
        interval: serializeDateRangeValue(dateRange),
      },
      tables: [
        {
          name: 'Table 1',
          query: { ...query.asJson(), version: queryVersion },
          options: {
            dataOptions: { excludeFalsy, includeTotals },
            dateOptions: { interval: null, tabSpecificDate: false },
            ...(columnOrder?.length ? { columnOrder } : {}),
          },
        },
      ],
      global_filters: {
        version: queryVersion,
        data: globalFilters
          .filter((gf: IQueryFilterWrapper) => gf.dataset === query.dataset)
          .map((gf: IQueryFilterWrapper) => ({
            dataset: gf.dataset,
            expressions: gf.expressions.map((e: QueryFilter) => e.asJson ? e.asJson() : e),
          })),
      },
    };

    this.trackingService.track('Open in Reports', { fromApp: this.sessionService.currentApp?.app });
    this.openInApp('reports', reportData);
  }

  public openInStudio({
    tables,
    dateRange,
    globalFilters = [],
    globalFiltersVersion = 1,
  }: OpenInStudioParams): void {
    if (!tables.length) {
      return;
    }

    const canvasPreset = { value: 'large-laptop', width: 1440, height: 900 };
    const { width: canvasW, height: canvasH } = canvasPreset;
    const colCount = 2;
    const cellW = canvasW / colCount;
    const cellH = canvasH / colCount;
    const widgetsPerPage = colCount * colCount;

    const tableToWidget = (
      table: OpenInStudioParams['tables'][number],
      gridIndex: number,
    ) => {
      const { query, excludeFalsy = false, showColumnAllPagesTotals = false, dateRange: tableDateRange, resultsPerPage, benchmark, benchmarkDateRange, columnOrder } = table;
      const col = gridIndex % colCount;
      const row = Math.floor(gridIndex / colCount);
      const queryJson = this.queryJsonForStudioTableWidget(query);
      const compareTo = this.buildCompareTo(benchmark, benchmarkDateRange);
      const normalizedResultsPerPage = resultsPerPage !== undefined
        ? (STUDIO_RESULTS_PER_PAGE_OPTIONS.includes(resultsPerPage) ? resultsPerPage : STUDIO_DEFAULT_RESULTS_PER_PAGE)
        : undefined;
      return {
        type: 'table' as const,
        layout: {
          x: col * cellW,
          y: row * cellH,
          width: cellW,
          height: cellH,
        },
        inputs: queryJson,
        query: queryJson,
        options: {
          dataOptions: { excludeFalsy, showColumnAllPagesTotals },
          ...(tableDateRange ? { dateRange: { enabled: true, value: serializeDateRangeValue(tableDateRange) } } : {}),
          ...(normalizedResultsPerPage !== undefined ? { resultsPerPage: { value: normalizedResultsPerPage, name: normalizedResultsPerPage } } : {}),
          ...(compareTo ? { compareTo } : {}),
          ...(columnOrder?.length ? { columnOrder } : {}),
        },
      };
    };

    const pages = [];
    for (let offset = 0; offset < tables.length; offset += widgetsPerPage) {
      const pageTables = tables.slice(offset, offset + widgetsPerPage);
      const pageIndex = offset / widgetsPerPage;
      pages.push({
        id: pageIndex,
        name: `Page ${pageIndex + 1}`,
        widgets: pageTables.map((table, gridIndex) => tableToWidget(table, gridIndex)),
      });
    }

    const datasetSet = new Set(
      tables
        .map(t => t.query?.dataset)
        .filter((d): d is string => typeof d === 'string' && d.length > 0),
    );

    const globalFiltersData = globalFilters
      .filter((gf: IQueryFilterWrapper) => datasetSet.has(gf.dataset))
      .map((gf: IQueryFilterWrapper) => ({
        dataset: gf.dataset,
        expressions: gf.expressions.map((e: QueryFilter) => (e.asJson ? e.asJson() : e)),
      }));

    const dashboardData = {
      name: 'New Dashboard',
      settings: {
        backgroundColor: '#ffffff',
        size: {
          preset: {
            value: canvasPreset.value,
            width: canvasPreset.width,
            height: canvasPreset.height,
          },
          width: undefined,
          height: undefined,
        },
        gridSize: {
          value: 20,
        },
        interval: serializeDateRangeValue(dateRange),
      },
      pages,
      global_filters: {
        version: globalFiltersVersion,
        data: globalFiltersData,
      },
    };

    this.trackingService.track('Open in Studio', { fromApp: this.sessionService.currentApp?.app });
    this.openInApp('studio', dashboardData);
  }

  public openInDiscovery({
    query,
    dateRange,
    excludeFalsy = false,
    globalFilters = [],
    resultsPerPage,
    benchmark,
    benchmarkDateRange,
  }: OpenInDiscoveryParams): void {
    const normalizedResultsPerPage = RESULTS_PER_PAGE_OPTIONS.find(o => o === resultsPerPage) ?? DEFAULT_RESULTS_PER_PAGE;
    const discoveryData = {
      name: 'New Discovery',
      query: this.discoveryQueryPayload(query, dateRange, globalFilters, benchmark, benchmarkDateRange),
      options: {
        dataOptions: { excludeFalsy },
        resultsPerPage: { value: normalizedResultsPerPage },
      },
    };

    this.trackingService.track('Open in Discovery', { fromApp: this.sessionService.currentApp?.app });
    this.openInApp('discovery', discoveryData);
  }

  private openInApp(app: 'reports' | 'studio' | 'discovery', payload: object): void {
    const baseUrl = this.sessionService.getAppBaseUrl(app);
    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      return;
    }

    this.publicLinkService.uuid().pipe(
      switchMap(({ uuid }) => this.publicLinkService.create(payload, uuid, `/s/${uuid}`).pipe(map(() => uuid))),
    ).subscribe({
      next: uuid => {
        tab.location.href = `//${baseUrl}/new?stash=${uuid}`;
      },
      error: () => tab.close(),
    });
  }

  private discoveryQueryPayload(
    query: Query,
    dateRange: DateRangeValue,
    globalFilters: IQueryFilterWrapper[] = [],
    benchmark?: string,
    benchmarkDateRange?: DateRangeValue,
  ) {
    const jsonQuery = query.asJson();
    const { order, filters, metric_filters: metricFilters, dimension_properties: dimensionProperties, ...rest } = jsonQuery;
    const advFilters = [
      ...(filters ?? []),
      ...this.serializedGlobalFiltersForDataset(query.dataset, globalFilters),
    ];
    return {
      ...rest,
      ...(dimensionProperties ? { dimensionProperties } : {}),
      order: this.orderForDiscoveryLink(order),
      ...(advFilters.length ? { advFilters } : {}),
      ...(metricFilters?.length ? { metricFilters } : {}),
      dates: serializeDateRangeValue(dateRange),
      drilldowns: {},
      ...(BENCHMARK_OPTIONS.some(o => o.value === benchmark && o.value !== DEFAULT_BENCHMARK_OPTION.value) ? { benchmark } : {}),
      ...(benchmarkDateRange ? { benchmarkDateRange: serializeDateRangeValue(benchmarkDateRange) } : {}),
    };
  }

  private serializedGlobalFiltersForDataset(dataset: string, globalFilters: IQueryFilterWrapper[]) {
    return (globalFilters ?? [])
      .filter(gf => !!dataset && gf.dataset === dataset && gf.expressions?.length)
      .map(gf => ({
        dataset: gf.dataset,
        expressions: gf.expressions.map((e: QueryFilter) => e.asJson()),
      }));
  }

  private orderForDiscoveryLink(order: string | string[] | undefined): string {
    if (typeof order === 'string') {
      return order;
    }
    if (Array.isArray(order)) {
      const first = order.find((o): o is string => typeof o === 'string' && o.length > 0);
      return first ?? '';
    }
    return '';
  }

  private buildCompareTo(benchmark?: string, benchmarkDateRange?: DateRangeValue) {
    const match = ALL_COMPARISONS.find(o => o.value === benchmark && o.value !== DEFAULT_COMPARE_TO.value);
    if (!match) {
      return null;
    }
    return {
      value: match.value,
      name: match.name,
      ...(benchmarkDateRange ? { dateRange: serializeDateRangeValue(benchmarkDateRange) } : {}),
    };
  }

  private queryJsonForStudioTableWidget(query: Query) {
    const json = query.asJson();
    if (json.filters?.length > 0) {
      return { ...json, version: Math.max(json.version ?? 0, 1) };
    }
    return json;
  }
}
